<template>
  <modal @close="onClose">
    <template #default>
      <div class="wrapper bookmark-add-menu__content">
        <ui-input
          v-model="bookmarkTitle"
          class="bookmark-add-menu__title-field"
          :label="editLabel"
          @keyup.enter="addBookmark"
        ></ui-input>
      </div>
    </template>

    <template #footer>
      <div class="bookmark-footer">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          @click="onClose"
        ></ui-button>

        <ui-button
          :text="t('Save')"
          button-style="accent"
          icon="fas fa-check"
          :disabled="disabled"
          @click="addBookmark(true)"
        ></ui-button>

        <ui-button
          v-if="edit"
          :text="t('Remove')"
          icon="fas fa-trash-alt"
          button-style="red"
          @click="removeBookmark"
        ></ui-button>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useStore } from '@/store';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { cloneDeep } from 'lodash';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { useTableQuery } from '@/composables/useTableQuery';

const store = useStore();

type BookmarkAddMenuData = {
  index?: number;
  item?: {
    id: string;
    tabId?: number;
    url: string;
    drill?: unknown;
  };
  title?: string;
};

const props = withDefaults(
  defineProps<{
    data?: BookmarkAddMenuData;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const route = useRoute();
const tableQueryApi = useTableQuery();

const index = ref(0);
const item = ref<BookmarkAddMenuData['item'] | null>(null);
const title = ref('');
const bookmarkTitle = ref('');

const edit = computed(() => index.value !== -1);

const editLabel = computed(() =>
  edit.value ? t('Edit a Bookmark') : t('Add a Bookmark'),
);

const disabled = computed(() => !bookmarkTitle.value);

const currentBookmark = computed(() => store.bookmarks[index.value] as Bemob.Bookmark);

const onClose = () => {
  emit('close');
};

const addBookmark = (forceCreate = false) => {
  if (disabled.value || !item.value) return;

  const url =
    item.value.tabId === tableQueryApi.tableActiveTab.value ? route.fullPath : item.value.url;

  const bookmark: Bemob.Bookmark = {
    bookmarkTitle: bookmarkTitle.value,
    url,
    id: item.value.id,
    drill: item.value.drill as Bemob.Bookmark['drill'],
    title: title.value,
    tabData: {
      tableQuery: cloneDeep({ ...tableQueryApi.tableQuery.value, date: 'customDateTime' }),
      tableQueryTemporary: cloneDeep({
        ...tableQueryApi.tableQueryTemporary.value,
        date: 'customDateTime',
      }),
    },
  };

  if (edit.value && !forceCreate) {
    store.editBookmark(index.value, bookmark);
    $emitter.emit('toast-success', t('Bookmark has been edited'));
  } else {
    store.addBookmark(bookmark);
    $emitter.emit('toast-success', t('Bookmark has been created'));
  }

  onClose();
};

const removeBookmark = () => {
  store.removeBookmark(index.value);
  $emitter.emit('toast-success', t('Bookmark has been removed'));
  onClose();
};

onMounted(() => {
  const { index: dataIndex = 0, item: dataItem = null, title: dataTitle = '' } = props.data;
  index.value = dataIndex;
  item.value = dataItem;
  title.value = dataTitle;
  bookmarkTitle.value = edit.value ? currentBookmark.value?.bookmarkTitle : title.value;
});
</script>

<style lang="scss" scoped>
  .wrapper {
    min-width: 300px;
  }

  .bookmark-footer {
    display: flex;
    justify-content: flex-end;

    :deep(.ui-button:last-child) {
      margin-right: 0;
    }
  }
</style>
